// ─── Role definitions ─────────────────────────────────────────────
export const EMPLOYEE_ROLES = [
  'Video Editor',
  'Graphic Designer',
  'Content Manager',
  'Performance Marketer - STR',
  'Performance Marketer',
  'Accounting Staff',
  'Company Accountant',
  'Scheduler',
];

// Which roles get which report access by default
export const ROLE_DEFAULT_PERMISSIONS = {
  'Content Manager':         { socialMedia: true,  perfMarketing: false },
  'Performance Marketer - STR': { socialMedia: false, perfMarketing: true  },
  'Performance Marketer':    { socialMedia: false, perfMarketing: true  },
  'Video Editor':            { socialMedia: false, perfMarketing: false },
  'Graphic Designer':        { socialMedia: false, perfMarketing: false },
  'Accounting Staff':        { socialMedia: false, perfMarketing: false },
  'Company Accountant':      { socialMedia: false, perfMarketing: false },
  'Scheduler':               { socialMedia: true,  perfMarketing: false },
};

// Client reporting capability types
export const CLIENT_AD_TYPES = ['E-Commerce', 'E-Commerce & DMs', 'Service Sector'];


// Ensures historical invoices are always present in the store
// (safe to call repeatedly — skips any that already exist by id)
function mergeHistoricalInvoices(existing) {
  const defaults = defaultInvoices();
  const existingIds = new Set(existing.map(i => i.id));
  const toAdd = defaults.filter(i => !existingIds.has(i.id));
  return toAdd.length > 0 ? [...existing, ...toAdd] : existing;
}


// ─── Data migration helpers ───────────────────────────────────────
// Called when loading old localStorage data that's missing new fields.

const CLIENT_FIELD_MAP = {
  c1:  { adType:'Service Sector',   socialMedia:true  },
  c2:  { adType:null,               socialMedia:true  },
  c3:  { adType:null,               socialMedia:false },
  c4:  { adType:null,               socialMedia:false,
    subAccounts:[
      {id:'c4a',name:'Signature by Eram',               adType:'Service Sector',socialMedia:true},
      {id:'c4b',name:'Signature Aesthetics & Wellness', adType:'Service Sector',socialMedia:true},
    ]
  },
  c5:  { adType:'Service Sector',   socialMedia:true  },
  c6:  { adType:'E-Commerce',       socialMedia:true  },
  c7:  { adType:'E-Commerce & DMs', socialMedia:true  },
  c8:  { adType:null,               socialMedia:true  },
  c9:  { adType:'E-Commerce',       socialMedia:false },
  c10: { adType:null,               socialMedia:false },
  c11: { adType:'Service Sector',   socialMedia:false },
  c12: { adType:'E-Commerce',       socialMedia:true  },
};

const EMP_ROLE_MAP = {
  emp1:{ role:'Video Editor',    reportPerms:{ socialMedia:false, perfMarketing:false } },
  emp2:{ role:'Graphic Designer',reportPerms:{ socialMedia:false, perfMarketing:false } },
  emp3:{ role:'Content Manager', roles:['Content Manager'], reportPerms:{ socialMedia:true,  perfMarketing:false } },
  emp4:{ role:'Video Editor',    reportPerms:{ socialMedia:false, perfMarketing:false } },
  emp5:{ role:'Graphic Designer', roles:['Graphic Designer'], reportPerms:{ socialMedia:true, perfMarketing:false } },
  emp6:{ role:'Content Manager', roles:['Content Manager'], reportPerms:{ socialMedia:true,  perfMarketing:false } },
};

function migrateClientFields(clients) {
  return clients.map(c => {
    const patch = CLIENT_FIELD_MAP[c.id] || {};
    const name  = c.name;
    // Handle Jade's Jam added by user dynamically
    if ((name === "Jades Jam" || name === "Jade's Jam") && c.adType === undefined) {
      return { ...c, name:"Jade's Jam", adType:'E-Commerce', socialMedia:false };
    }
    return { ...c, ...patch };
  });
}

function migrateEmployeeFields(employees) {
  return employees.map(e => {
    const patch = EMP_ROLE_MAP[e.id] || { role:'Staff', roles:['Staff'], reportPerms:{ socialMedia:false, perfMarketing:false } };
    const merged = { ...e, ...patch };
    // Always ensure roles array is populated
    if (!merged.roles || merged.roles.length === 0) {
      merged.roles = [merged.role || 'Staff'];
    }
    return merged;
  });
}

export function defaultTheme() {
  // STR — Strategists brand identity (obsidian ground, graphite panels, brass accent)
  return { accent:'#A98953', bg:'#0A0A0C', bg2:'#141417', bg3:'#17171A', bg4:'#20201d', font:'Inter Tight', borderRadius:'2px', mode:'dark' };
}

function defaultStore() {
  return {
    clients:            defaultClients(),
    invoices:           defaultInvoices(),
    employees:          defaultEmployees(),
    salaryPayments:     [],
    subscriptions:      [],
    oneTimeExpenses:    [],
    oneTimeRevenue:     [],
    commissionPayments: [],
    hr:                 defaultHR(),
    theme:              defaultTheme(),
    employeeCredentials: defaultCredentials(),
    leaveRequests:      [],
    updates:            [],
    notifications:      [], // { id, empId(recipient), message, taskId, kind, createdAt, read }
    holidays:           null,
    reports:            [], // { id, clientId, type:'social'|'performance', monthYear, data:{...} }
    parentClients:      defaultParentClients(),
    overtimeRequests:   [], // employee overtime claim requests
    tasks:              [], // task manager entries
  };
}

function defaultClients() {
  // adType: null = no ads, 'E-Commerce'|'E-Commerce & DMs'|'Service Sector'
  // socialMedia: bool
  return [
    { id:'c1',  name:'Padel Edge',                amount:137000, dueDay:1,  type:'fixed', adType:'Service Sector',    socialMedia:true  },
    { id:'c2',  name:'HAAF Group',                amount:40000,  dueDay:1,  type:'fixed', adType:null,               socialMedia:true  },
    { id:'c3',  name:'Dhaanak by Mahnoor',        amount:35000,  dueDay:1,  type:'fixed', adType:null,               socialMedia:false },
    { id:'c4',  name:'Eram Associates',           amount:250000, dueDay:1,  type:'fixed', adType:null,               socialMedia:false,
      subAccounts:[
        { id:'c4a', name:'Signature by Eram',               adType:'Service Sector', socialMedia:true  },
        { id:'c4b', name:'Signature Aesthetics & Wellness', adType:'Service Sector', socialMedia:true  },
      ]
    },
    { id:'c5',  name:'ISov',                      amount:50000,  dueDay:1,  type:'fixed', adType:'Service Sector',    socialMedia:true  },
    { id:'c6',  name:'Fabrizia',                  amount:70000,  dueDay:7,  type:'fixed', adType:'E-Commerce',        socialMedia:true  },
    { id:'c7',  name:'Amina Khalid',              amount:55000,  dueDay:14, type:'fixed', adType:'E-Commerce & DMs',  socialMedia:true  },
    { id:'c8',  name:'Vertex Chemicals',          amount:30000,  dueDay:1,  type:'fixed', adType:null,               socialMedia:true  },
    { id:'c9',  name:'My Sewing Mall',            amount:115000, dueDay:12, type:'fixed', adType:'E-Commerce',        socialMedia:false, parentClientId:'tpc' },
    { id:'c10', name:'LYM Real Estate',           amount:45000,  dueDay:1,  type:'fixed', adType:null,               socialMedia:false },
    { id:'c11', name:'Atom Inspections',          amount:115000, dueDay:1,  type:'fixed', adType:'Service Sector',    socialMedia:false, parentClientId:'tpc' },
    { id:'c12', name:'Rashid Fabrics',            amount:null,   dueDay:1,  type:'commission', commissionRate:5, adType:'E-Commerce', socialMedia:true },
    { id:'c13', name:"Jade's Jam",                amount:35000,  dueDay:1,  type:'fixed', adType:'E-Commerce',        socialMedia:false },
    { id:'c14', name:'Padel Up',                  amount:80000,  dueDay:1,  type:'fixed', adType:'Service Sector',    socialMedia:false, active:false, formerClient:true },
    { id:'c15', name:'LYM (Stays Dxb / Luqman)',  amount:45000,  dueDay:1,  type:'fixed', adType:'Service Sector',    socialMedia:true,  parentClientId:'tpc', active:true },
  ];
}


function defaultInvoices() {
  // Pre-seeded historical and outstanding invoices
  // Padel Up (c14): Aug 2025 + Sep 2025, PKR 80,000 each, unpaid
  // ISov     (c5):  Feb + Mar + Apr 2026, PKR 50,000 each, unpaid
  // Jade's Jam (c13): Jan 2026 full, Feb + Mar 2026 half (17,500), unpaid
  // Vertex (c8): 4 invoices (billing period style), last one paid
  return [
    // ── Padel Up ─────────────────────────────────────────────────
    { id:'hist-pu-1', clientId:'c14', amount:80000, status:'pending',
      monthYear:'2025-08', invoiceNumber:'INV-000001',
      generatedDate:'2025-08-01', notes:'Outstanding — former client', isHistorical:true },
    { id:'hist-pu-2', clientId:'c14', amount:80000, status:'pending',
      monthYear:'2025-09', invoiceNumber:'INV-000002',
      generatedDate:'2025-09-01', notes:'Outstanding — former client', isHistorical:true },

    // ── ISov ─────────────────────────────────────────────────────
    { id:'hist-isov-1', clientId:'c5', amount:50000, status:'pending',
      monthYear:'2026-02', invoiceNumber:'INV-000003',
      generatedDate:'2026-02-01', isHistorical:true },
    { id:'hist-isov-2', clientId:'c5', amount:50000, status:'pending',
      monthYear:'2026-03', invoiceNumber:'INV-000004',
      generatedDate:'2026-03-01', isHistorical:true },
    { id:'hist-isov-3', clientId:'c5', amount:50000, status:'pending',
      monthYear:'2026-04', invoiceNumber:'INV-000005',
      generatedDate:'2026-04-01', isHistorical:true },

    // ── Jade's Jam ───────────────────────────────────────────────
    { id:'hist-jj-1', clientId:'c13', amount:35000, status:'pending',
      monthYear:'2026-01', invoiceNumber:'INV-000006',
      generatedDate:'2026-01-01', isHistorical:true },
    { id:'hist-jj-2', clientId:'c13', amount:17500, status:'pending',
      monthYear:'2026-02', invoiceNumber:'INV-000007',
      generatedDate:'2026-02-01', notes:'Half amount charged', isHistorical:true },
    { id:'hist-jj-3', clientId:'c13', amount:17500, status:'pending',
      monthYear:'2026-03', invoiceNumber:'INV-000008',
      generatedDate:'2026-03-01', notes:'Half amount charged', isHistorical:true },

    // ── LYM / Stays Dxb (Parthenon) ─────────────────────────────
    { id:'lym-001', clientId:'c15', amount:184950, status:'paid', monthYear:'2024-08', invoiceNumber:'LYM001', generatedDate:'2024-08-01', paidDate:'2024-09-01', notes:'Monthly Retainer & Hassan Fee', isHistorical:true },
    { id:'lym-002', clientId:'c15', amount:153000, status:'paid', monthYear:'2024-09', invoiceNumber:'LYM002', generatedDate:'2024-09-01', paidDate:'2024-10-01', notes:'Monthly Retainer & Hassan Fee', isHistorical:true },
    { id:'lym-003', clientId:'c15', amount:238000,  status:'paid', monthYear:'2024-10', invoiceNumber:'LYM003', generatedDate:'2024-10-01', notes:'Monthly Retainer, Hassan Fee + Salary', paidDate:'2024-11-15', isHistorical:true },
    { id:'lym-004', clientId:'c15', amount:197650,  status:'paid', monthYear:'2024-11', invoiceNumber:'LYM004', generatedDate:'2024-11-01', notes:'Monthly Retainer & Hassan Fee', paidDate:'2024-12-15', isHistorical:true },
    { id:'lym-005', clientId:'c15', amount:239800,  status:'paid', monthYear:'2024-12', invoiceNumber:'LYM005', generatedDate:'2024-12-01', notes:'Monthly Retainer & Hassan Fee', paidDate:'2025-01-15', isHistorical:true },
    { id:'lym-006', clientId:'c15', amount:285100,  status:'paid', monthYear:'2025-01', invoiceNumber:'LYM006', generatedDate:'2025-01-01', notes:'Monthly Retainer, Social Media, Hassan Salary & Fee', paidDate:'2025-02-15', isHistorical:true },
    { id:'lym-007', clientId:'c15', amount:285100,  status:'paid', monthYear:'2025-02', invoiceNumber:'LYM007', generatedDate:'2025-02-01', notes:'Monthly Retainer, Social Media, Hassan Salary & Fee', paidDate:'2025-03-15', isHistorical:true },
    { id:'lym-008', clientId:'c15', amount:285100,  status:'paid', monthYear:'2025-03', invoiceNumber:'LYM008', generatedDate:'2025-03-01', notes:'Monthly Retainer, Social Media, Hassan Salary & Fee', paidDate:'2025-04-15', isHistorical:true },
    { id:'lym-009', clientId:'c15', amount:285100,  status:'paid', monthYear:'2025-04', invoiceNumber:'LYM009', generatedDate:'2025-04-01', notes:'Monthly Retainer, Social Media, Hassan Salary & Fee', paidDate:'2025-05-15', isHistorical:true },
    { id:'lym-010', clientId:'c15', amount:285100,  status:'paid', monthYear:'2025-05', invoiceNumber:'LYM010', generatedDate:'2025-05-01', notes:'Monthly Retainer, Social Media, Hassan Salary & Fee', paidDate:'2025-06-15', isHistorical:true },
    { id:'lym-011', clientId:'c15', amount:210000,  status:'pending', monthYear:'2025-06', invoiceNumber:'LYM011', generatedDate:'2025-06-01', notes:'Hassan Salary, Fee & LYM Social Media / SEO', isHistorical:true },
    { id:'lym-012', clientId:'c15', amount:200000,  status:'pending', monthYear:'2025-07', invoiceNumber:'LYM012', generatedDate:'2025-07-01', notes:'Hassan Salary, Fee & LYM Social Media / SEO', isHistorical:true },
    { id:'lym-013', clientId:'c15', amount:165000,  status:'pending', monthYear:'2025-08', invoiceNumber:'LYM013', generatedDate:'2025-08-01', notes:'Hassan Salary, Hassan Fee & LYM Social Media', isHistorical:true },
    { id:'lym-misc', clientId:'c15', amount:10000,  status:'pending', monthYear:'2025-08', invoiceNumber:'LYM-MISC', generatedDate:'2025-08-01', notes:'Heera Dues', isHistorical:true },
    { id:'lym-014', clientId:'c15', amount:45000,   status:'pending', monthYear:'2025-09', invoiceNumber:'LYM014', generatedDate:'2025-09-01', notes:'Social Media', isHistorical:true },
    { id:'lym-015', clientId:'c15', amount:45000,   status:'pending', monthYear:'2025-10', invoiceNumber:'LYM015', generatedDate:'2025-10-01', notes:'Social Media', isHistorical:true },
    { id:'lym-016', clientId:'c15', amount:45000,   status:'pending', monthYear:'2025-11', invoiceNumber:'LYM016', generatedDate:'2025-11-01', notes:'Social Media', isHistorical:true },
    { id:'lym-017', clientId:'c15', amount:45000,   status:'pending', monthYear:'2025-12', invoiceNumber:'LYM017', generatedDate:'2025-12-01', notes:'Social Media (note: listed as MSM billed-to in sheet — treated as LYM by sequence)', isHistorical:true },
    { id:'lym-018', clientId:'c15', amount:45000,   status:'pending', monthYear:'2026-01', invoiceNumber:'LYM018', generatedDate:'2026-01-01', notes:'Social Media', isHistorical:true },
    { id:'lym-019', clientId:'c15', amount:45000,   status:'pending', monthYear:'2026-02', invoiceNumber:'LYM019', generatedDate:'2026-02-01', notes:'Social Media', isHistorical:true },
    { id:'lym-020', clientId:'c15', amount:45000,   status:'pending', monthYear:'2026-03', invoiceNumber:'LYM020', generatedDate:'2026-03-01', notes:'Social Media', isHistorical:true },

    // ── MSM / My Sewing Mall (Parthenon) — billing period = end month ─
    { id:'msm-001', clientId:'c9', amount:115000, status:'paid', monthYear:'2025-05', invoiceNumber:'MSM001', generatedDate:'2025-05-01', paidDate:'2025-06-01', notes:'Google Ads (Apr–May 2025)', isHistorical:true },
    { id:'msm-002', clientId:'c9', amount:115000, status:'paid', monthYear:'2025-06', invoiceNumber:'MSM002', generatedDate:'2025-06-01', paidDate:'2025-07-01', notes:'Google Ads (May–Jun 2025)', isHistorical:true },
    { id:'msm-003', clientId:'c9', amount:115000, status:'paid', monthYear:'2025-07', invoiceNumber:'MSM003', generatedDate:'2025-07-01', paidDate:'2025-08-01', notes:'Google Ads (Jun–Jul 2025)', isHistorical:true },
    { id:'msm-004', clientId:'c9', amount:115000, status:'paid', monthYear:'2025-06', invoiceNumber:'MSM004', generatedDate:'2025-06-15', paidDate:'2025-07-01', notes:'Website starting payment', isHistorical:true },
    { id:'msm-005', clientId:'c9', amount:115000, status:'pending', monthYear:'2025-08', invoiceNumber:'MSM005', generatedDate:'2025-08-01', notes:'Google Ads (Jul–Aug 2025)', isHistorical:true },
    { id:'msm-006', clientId:'c9', amount:86250,  status:'pending', monthYear:'2025-08', invoiceNumber:'MSM006', generatedDate:'2025-08-15', notes:'Website delivery payment', isHistorical:true },
    { id:'msm-007', clientId:'c9', amount:115000, status:'pending', monthYear:'2025-09', invoiceNumber:'MSM007', generatedDate:'2025-09-01', notes:'Google Ads (Aug–Sep 2025)', isHistorical:true },
    { id:'msm-008a', clientId:'c9', amount:115000, status:'pending', monthYear:'2025-10', invoiceNumber:'MSM008', generatedDate:'2025-10-01', notes:'Google Ads (Sep–Oct 2025)', isHistorical:true },
    { id:'msm-009a', clientId:'c9', amount:115000, status:'pending', monthYear:'2025-11', invoiceNumber:'MSM009', generatedDate:'2025-11-01', notes:'Google Ads (Oct–Nov 2025)', isHistorical:true },
    { id:'msm-008b', clientId:'c9', amount:115000, status:'pending', monthYear:'2025-12', invoiceNumber:'MSM008B', generatedDate:'2025-12-01', notes:'Google Ads (Nov–Dec 2025)', isHistorical:true },
    { id:'msm-009b', clientId:'c9', amount:115000, status:'pending', monthYear:'2026-01', invoiceNumber:'MSM009B', generatedDate:'2026-01-01', notes:'Google Ads (Dec 2025–Jan 2026)', isHistorical:true },
    { id:'msm-010a', clientId:'c9', amount:115000, status:'pending', monthYear:'2026-02', invoiceNumber:'MSM010', generatedDate:'2026-02-01', notes:'Google Ads (Jan–Feb 2026)', isHistorical:true },
    { id:'msm-010b', clientId:'c9', amount:115000, status:'pending', monthYear:'2026-03', invoiceNumber:'MSM010B', generatedDate:'2026-03-01', notes:'Google Ads (Feb–Mar 2026)', isHistorical:true },
    { id:'msm-011', clientId:'c9', amount:115000,  status:'pending', monthYear:'2026-04', invoiceNumber:'MSM011', generatedDate:'2026-04-01', notes:'Google Ads (Mar–Apr 2026)', isHistorical:true },
    { id:'msm-012', clientId:'c9', amount:115000,  status:'pending', monthYear:'2026-05', invoiceNumber:'MSM012', generatedDate:'2026-05-01', notes:'Google Ads (Apr–May 2026)', isHistorical:true },

    // ── Atom Inspections (Parthenon) ──────────────────────────────
    { id:'ai-001', clientId:'c11', amount:115000, status:'pending', monthYear:'2026-04', invoiceNumber:'AI001', generatedDate:'2026-04-01', notes:'Google Ads', isHistorical:true },
    { id:'ai-002', clientId:'c11', amount:115000, status:'pending', monthYear:'2026-05', invoiceNumber:'AI002', generatedDate:'2026-05-01', notes:'Google Ads', isHistorical:true },

    // ── Vertex Chemicals ─────────────────────────────────────────
    // Billing period format: each covers a month-spanning window
    { id:'hist-vc-1', clientId:'c8', amount:30000, status:'pending',
      monthYear:'2025-12', invoiceNumber:'INV-000009',
      generatedDate:'2025-12-01', notes:'Billing period: Nov 2025 – Dec 2025', isHistorical:true },
    { id:'hist-vc-2', clientId:'c8', amount:30000, status:'pending',
      monthYear:'2026-01', invoiceNumber:'INV-000010',
      generatedDate:'2026-01-01', notes:'Billing period: Dec 2025 – Jan 2026', isHistorical:true },
    { id:'hist-vc-3', clientId:'c8', amount:30000, status:'pending',
      monthYear:'2026-02', invoiceNumber:'INV-000011',
      generatedDate:'2026-02-01', notes:'Billing period: Jan 2026 – Feb 2026', isHistorical:true },
    { id:'hist-vc-4', clientId:'c8', amount:30000, status:'paid',
      monthYear:'2026-03', invoiceNumber:'INV-000012',
      generatedDate:'2026-03-01', paidDate:'2026-03-15',
      notes:'Billing period: Feb 2026 – Mar 2026', isHistorical:true },
  ];
}

function defaultParentClients() {
  return [
    {
      id: 'tpc',
      name: 'The Parthenon Consultancy',
      contactName: '',
      contactEmail: '',
      notes: 'Billing agency for MSM, Atom Inspections, and LYM (Stays Dxb / Luqman). Total invoiced: PKR 5,265,150 | Payments received: PKR 2,931,000 | Outstanding per sheet: PKR 2,334,150',
      creditBalance: 32100,  // PKR leftover after applying 2,931,000 chronologically
      subClients: [
        { id:'c9',  name:'My Sewing Mall',             amount:115000, dueDay:12, startDate:'2025-04', adType:'E-Commerce',     socialMedia:false, active:true },
        { id:'c11', name:'Atom Inspections',            amount:115000, dueDay:1,  startDate:'2026-04', adType:'Service Sector', socialMedia:false, active:true },
        { id:'c15', name:'LYM (Stays Dxb / Luqman)',   amount:45000,  dueDay:1,  startDate:'2024-10', adType:'Service Sector', socialMedia:true,  active:true },
      ],
      projects: [],
    },
  ];
}

function defaultEmployees() {
  return [
    { id:'emp1', name:'Affan Khan',     amount:110000, dueDay:1,  type:'fixed', role:'Video Editor',    reportPerms:{ socialMedia:false, perfMarketing:false } },
    { id:'emp2', name:'Hamza Ashiq',    amount:72500,  dueDay:1,  type:'fixed', role:'Graphic Designer', reportPerms:{ socialMedia:false, perfMarketing:false } },
    { id:'emp3', name:'Wajiha Amir',    amount:90000,  dueDay:1,  type:'fixed', role:'Content Manager',  reportPerms:{ socialMedia:true,  perfMarketing:false } },
    { id:'emp4', name:'Shoaib Ramzan',  amount:40000,  dueDay:5,  type:'fixed', role:'Video Editor',    reportPerms:{ socialMedia:false, perfMarketing:false } },
    { id:'emp5', name:'Muhammad Numan', amount:45000,  dueDay:8,  type:'fixed', role:'Graphic Designer', reportPerms:{ socialMedia:false, perfMarketing:false } },
    { id:'emp6', name:'Isha Tahir',     amount:55000,  dueDay:19, type:'fixed', role:'Content Manager',  reportPerms:{ socialMedia:true,  perfMarketing:false } },
  ];
}

function defaultHR() {
  return {
    emp1:{ workHours:{ start:'09:00', end:'17:00' } },
    emp2:{ workHours:{ start:'12:00', end:'19:30' } },
    emp3:{ workHours:{ start:'10:00', end:'19:00' } },
    emp4:{ workHours:{ start:'09:00', end:'18:00' } },
    emp5:{ workHours:{ start:'12:00', end:'19:30' } },
    emp6:{ workHours:{ start:'10:00', end:'19:00' } },
  };
}

// Staff sign-ins live in Supabase Auth (passwords are never part of the app's data).
function defaultCredentials() {
  return {};
}
